/////////////////////////////////////////////////////////
// Configurations related to listing.                  //
// Main configuration here is the extended data config //
/////////////////////////////////////////////////////////

// Note: The listingFields come from listingFields asset nowadays by default.
//       To use this built-in configuration, you need to change the overwrite from configHelper.js
//       (E.g. use mergeDefaultTypesAndFieldsForDebugging func)

/**
 * Configuration options for listing fields (custom extended data fields):
 * - key:                           Unique key for the extended data field.
 * - scope (optional):              Scope of the extended data can be either 'public' or 'private'.
 *                                  Default value: 'public'.
 *                                  Note: listing doesn't support 'protected' scope atm.
 * - includeForListingTypes:        An array of listing types, for which the extended
 *   (optional)                     data is relevant and should be added.
 * - schemaType (optional):         Schema for this extended data field.
 *                                  This is relevant when rendering components and querying listings.
 *                                  Possible values: 'enum', 'multi-enum', 'text', 'long', 'boolean'.
 * - enumOptions (optional):        Options shown for 'enum' and 'multi-enum' extended data.
 *                                  These are used to render options for inputs and filters on
 *                                  EditListingPage, ListingPage, and SearchPage.
 * - filterConfig:                  Filter configuration for listings query.
 *    - indexForSearch (optional):    If set as true, it is assumed that the extended data key has
 *                                    search index in place. I.e. the key can be used to filter
 *                                    listing queries (then scope needs to be 'public').
 *                                    Note: Flex CLI can be used to set search index for the key:
 *                                    https://www.sharetribe.com/docs/references/extended-data/#search-schema
 *                                    Read more about filtering listings with public data keys from API Reference:
 *                                    https://www.sharetribe.com/api-reference/marketplace.html#extended-data-filtering
 *                                    Default value: false,
 *   - filterType:                    Sometimes a single schemaType can be rendered with different filter components.
 *                                    For 'enum' schema, filterType can be 'SelectSingleFilter' or 'SelectMultipleFilter'
 *   - label:                         Label for the filter, if the field can be used as query filter
 *   - searchMode (optional):         Search mode for indexed data with multi-enum schema.
 *                                    Possible values: 'has_all' or 'has_any'.
 *   - group:                         SearchPageWithMap has grouped filters. Possible values: 'primary' or 'secondary'.
 * - showConfig:                    Configuration for rendering listing. (How the field should be shown.)
 *   - label:                         Label for the saved data.
 *   - isDetail                       Can be used to hide detail row (of type enum, boolean, or long) from listing page.
 *                                    Default value: true,
 * - saveConfig:                    Configuration for adding and modifying extended data fields.
 *   - label:                         Label for the input field.
 *   - placeholderMessage (optional): Default message for user input.
 *   - isRequired (optional):         Is the field required for providers to fill
 *   - requiredMessage (optional):    Message for those fields, which are mandatory.
 */
export const listingFields = [
  {
    key: 'category',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'city-bikes', label: 'City bikes' },
      { option: 'electric-bikes', label: 'Electric bikes' },
      { option: 'mountain-bikes', label: 'Mountain bikes' },
      { option: 'childrens-bikes', label: "Children's bikes" },
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Category',
      group: 'primary',
    },
    showConfig: {
      label: 'Category',
      isDetail: true,
    },
    saveConfig: {
      label: 'Category',
      placeholderMessage: 'Select an option…',
      isRequired: true,
      requiredMessage: 'You need to select a category.',
    },
  },
  {
    key: 'tire',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '29', label: '29' },
      { option: '28', label: '28' },
      { option: '27', label: '27' },
      { option: '26', label: '26' },
      { option: '24', label: '24' },
      { option: '20', label: '20' },
      { option: '18', label: '18' },
    ],
    filterConfig: {
      indexForSearch: true,
      label: 'Tire size',
      group: 'secondary',
    },
    showConfig: {
      label: 'Tire size',
      isDetail: true,
    },
    saveConfig: {
      label: 'Tire size',
      placeholderMessage: 'Select an option…',
      isRequired: true,
      requiredMessage: 'You need to select a tire size.',
    },
  },
  {
    key: 'brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'cube', label: 'Cube' },
      { option: 'diamant', label: 'Diamant' },
      { option: 'ghost', label: 'GHOST' },
      { option: 'giant', label: 'Giant' },
      { option: 'kalkhoff', label: 'Kalkhoff' },
      { option: 'kona', label: 'Kona' },
      { option: 'otler', label: 'Otler' },
      { option: 'vermont', label: 'Vermont' },
    ],
    filterConfig: {
      indexForSearch: true,
      label: 'Brand',
      group: 'secondary',
    },
    showConfig: {
      label: 'Brand',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand',
      placeholderMessage: 'Select an option…',
      isRequired: true,
      requiredMessage: 'You need to select a brand.',
    },
  },
  {
    key: 'accessories',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'bell', label: 'Bell' },
      { option: 'lights', label: 'Lights' },
      { option: 'lock', label: 'Lock' },
      { option: 'mudguard', label: 'Mudguard' },
    ],
    filterConfig: {
      indexForSearch: true,
      label: 'Accessories',
      searchMode: 'has_all',
      group: 'secondary',
    },
    showConfig: {
      label: 'Accessories',
    },
    saveConfig: {
      label: 'Accessories',
      placeholderMessage: 'Select an option…',
      isRequired: false,
    },
  },

  // // An example of how to use transaction type specific custom fields and private data.
  // {
  //   key: 'note',
  //   scope: 'public',
  //   includeForListingTypes: ['product-selling'],
  //   schemaType: 'text',
  //   showConfig: {
  //     label: 'Extra notes',
  //   },
  //   saveConfig: {
  //     label: 'Extra notes',
  //     placeholderMessage: 'Some public extra note about this bike...',
  //   },
  // },
  // {
  //   key: 'privatenote',
  //   scope: 'private',
  //   includeForListingTypes: ['daily-booking'],
  //   schemaType: 'text',
  //   saveConfig: {
  //     label: 'Private notes',
  //     placeholderMessage: 'Some private note about this bike...',
  //   },
  // },
];

export const additionalFees = [
  {
    key: "feeType",
    scope: "public",
    schemaType: 'multi-enum',
    heading: "Fee Type",
    enumOptions: [
      { label: "Delivery Fee", option: "deliveryFee", disabled: false, hidden: true },
      { label: "Pickup Fee", option: "pickupFee", disabled: false, hidden: true },
      { label: "Shipping Fee", option: "shippingFee", disabled: false },
      { label: "Cleaning Fee", option: "cleaningFee", disabled: false },
      { label: "Setup Fee", option: "setupFee", disabled: false },
      { label: "Environmental Fee", option: "environmentalFee", disabled: false },
      { label: "Fuel Fee", option: "fuelFee", disabled: false },
      { label: "Usage Fee", option: "usageFee", disabled: false },
      { label: "Accessory Fee", option: "accessoryFee", disabled: false },
      { label: "Consumables Fee", option: "consumablesFee", disabled: false },
      { label: "Custom Fee (with custom text input)", option: "customFee", disabled: true }
    ]
  },
  {
    key: "whenToApply",
    scope: "public",
    schemaType: 'multi-enum',
    heading: "Select when to apply",
    enumOptions: [
      { label: "Optional When Booking", option: "requiredUpfrontOptional", disabled: false },
      { label: "Required When Booking", option: "requiredUpfront", disabled: false },
      // { label: "Optional Throughout the Booking (coming soon)", option: "optionalThroughout", disabled: true },
    ]
  },
  {
    key: "feeCalculationType",
    scope: "public",
    schemaType: 'multi-enum',
    heading: "Fee Calculation Type",
    enumOptions: [
      { label: "Flat Fee $", option: "flatRate", disabled: false },
      { label: "% of rental amount", option: "percentageOfRental", disabled: false },
      { label: "Usage multiple", option: "usageMultiple", disabled: false }
    ]
  },
  {
    key: "checkoutOptions",
    scope: "public",
    schemaType: 'multi-enum',
    heading: "Checkout Option",
    enumOptions: [
      { label: "Only charge once for the entire cart", option: "oncePerCart", disabled: false },
      { label: "Charge For This Item Individually", option: "oncePerItem", disabled: false }
      // { label: "Charged once per listing", option: "oncePerListing", disabled: false },
    ]
  }
];

///////////////////////////////////////////////////////////////////////
// Configurations related to listing types and transaction processes //
///////////////////////////////////////////////////////////////////////

// A presets of supported listing configurations
//
// Note 1: The listingTypes come from listingTypes asset nowadays by default.
//         To use this built-in configuration, you need to change the overwrite from configHelper.js
//         (E.g. use mergeDefaultTypesAndFieldsForDebugging func)
// Note 2: transaction type is part of listing type. It defines what transaction process and units
//         are used when transaction is created against a specific listing.

/**
 * Configuration options for listing experience:
 * - listingType:         Unique string. This will be saved to listing's public data on
 *                        EditListingWizard.
 * - label                Label for the listing type. Used as microcopy for options to select
 *                        listing type in EditListingWizard.
 * - transactionType      Set of configurations how this listing type will behave when transaction is
 *                        created.
 *   - process              Transaction process.
 *                          The process must match one of the processes that this client app can handle
 *                          (check src/util/transactions/transaction.js) and the process must also exists in correct
 *                          marketplace environment.
 *   - alias                Valid alias for the aforementioned process. This will be saved to listing's
 *                          public data as transctionProcessAlias and transaction is initiated with this.
 *   - unitType             Unit type is mainly used as pricing unit. This will be saved to
 *                          transaction's protected data.
 *                          Recommendation: don't use same unit types in completely different processes
 *                          ('item' sold should not be priced the same as 'item' booked).
 * - stockType            This is relevant only to listings using default-purchase process.
 *                        If set to 'oneItem', stock management is not showed and the listing is
 *                        considered unique (stock = 1).
 *                        Possible values: 'oneItem' and 'multipleItems'.
 *                        Default: 'multipleItems'.
 * - defaultListingFields These are tied to transaction processes. Different processes have different flags.
 *                        E.g. default-inquiry can toggle price and location to true/false value to indicate,
 *                        whether price (or location) tab should be shown. If defaultListingFields.price is not
 *                        explicitly set to _false_, price will be shown.
 *                        If the location or pickup is not used, listing won't be returned with location search.
 *                        Use keyword search as main search type if location is not enforced.
 *                        The payoutDetails flag allows provider to bypass setting of payout details.
 *                        Note: customers can't order listings, if provider has not set payout details! Monitor
 *                        providers who have not set payout details and contact them to ensure that they add the details.
 */

export const listingTypes = [
  {
    listingType: 'daily-booking',
    label: 'Daily booking',
    transactionType: {
      process: 'default-booking',
      alias: 'default-booking/release-1',
      unitType: 'day',
    },
    defaultListingFields: {
      location: true,
      payoutDetails: true,
    },
  },
  // // Here are some examples for other listingTypes
  // // TODO: SearchPage does not work well if both booking and product selling are used at the same time
  // {
  //   listingType: 'nightly-booking',
  //   label: 'Nightly booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'night',
  //   },
  // },
  // {
  //   listingType: 'hourly-booking',
  //   label: 'Hourly booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'hour',
  //   },
  // },
  // {
  //   listingType: 'product-selling',
  //   label: 'Sell bicycles',
  //   transactionType: {
  //     process: 'default-purchase',
  //     alias: 'default-purchase/release-1',
  //     unitType: 'item',
  //   },
  //   stockType: 'multipleItems',
  //   defaultListingFields: {
  //     shipping: true,
  //     pickup: true,
  //     payoutDetails: true,
  //   },
  // },
  // {
  //   listingType: 'inquiry',
  //   label: 'Inquiry',
  //   transactionType: {
  //     process: 'default-inquiry',
  //     alias: 'default-inquiry/release-1',
  //     unitType: 'inquiry',
  //   },
  //   defaultListingFields: {
  //     price: false,
  //     location: true,
  //   },
  // },
];

// SearchPage can enforce listing query to only those listings with valid listingType
// However, it only works if you have set 'enum' type search schema for the public data fields
//   - listingType
//
//  Similar setup could be expanded to 2 other extended data fields:
//   - transactionProcessAlias
//   - unitType
//
// Read More:
// https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
export const enforceValidListingType = false;

// Change options in server/api/data if you want to change the options here
export const aiListingFields = [
  {
    key: "additionalFees",
    scope: "public",
    schemaType: "array",
    fields: additionalFees,
    saveConfig: {
      sectionTitle: "Additional Fees",
      label: "Additional Fees",
      showRegenerate: false,
      showToggle: false,
      showCredits: false,
      showChevron: true,
    },
    showConfig: {
      label: "Additional Fees",
      isDetail: true,
    },
    includeForListingTypes: [
      "general"
    ]
  },
  {
    key: "title",
    schemaType: "text",
    saveConfig: { sectionTitle: 'Listing Title', label: 'Create a clear descriptive title for your listing', isRequired: true, requiredMessage: 'Title is required (min 3 / max 60 characters)', showRegenerate: true, showChevron: true, showToggle: false, min: 3, max: 60 },
    showConfig: { label: 'Listing Title', isDetail: true },
  },
  {
    key: "description",
    schemaType: "text",
    saveConfig: { sectionTitle: 'Listing Description', label: 'Describe your item in detail to attract potential renters', isRequired: true, requiredMessage: 'Description is required (min 10 / max 3000 characters)', showRegenerate: true, showChevron: true, showToggle: false, min: 10, max: 3000 },
    showConfig: { label: 'Listing Description', isDetail: true },
  },
  {
    key: "instructions",
    scope: "public",
    schemaType: "text",
    filterConfig: {
      indexForSearch: false,
      label: "How To Use Instructions",
      group: "primary"
    },
    showConfig: {
      label: "How To Use Instructions",
      isDetail: true,
    },
    saveConfig: {
      sectionTitle: "How To Use Instructions",
      label: "Provide instructions for using your item",
      placeholder: "Provide instructions for using your item",
      isRequired: false,
      showRegenerate: true,
      showToggle: true,
      showCredits: true,
      showChevron: true,
      max: 2000,
      maxLengthMessage: 'Maximum length is 2000'
    },
    includeForListingTypes: [
      "general"
    ]
  },
  {
    key: "category",
    scope: "public",
    schemaType: "enum",
    enumOptions: [
      {
        label: "Animals & Accessories",
        option: "AnimalsAccessories",
        icon: "bear-smile-line",
        labels: { en: "Animals & Accessories", fr: "Animaux & accessoires", es: "Animales y accesorios", zh: "宠物与配件" }
      },
      {
        label: "Equipment & Tools",
        option: "Equipment-Tools",
        icon: "tools-line",
        labels: { en: "Equipment & Tools", fr: "Équipement & outils", es: "Equipos y herramientas", zh: "设备与工具" }
      },
      {
        label: "Electronics, Furniture & Home",
        option: "Electronics-Furniture",
        icon: "sofa-line",
        labels: { en: "Electronics, Furniture & Home", fr: "Électronique, meubles et maison", es: "Electrónica, muebles y hogar", zh: "电子产品、家具与家居" }
      },
      {
        label: "Fashion & Accessories",
        option: "Fashion-Accessories",
        icon: "shirt-line",
        labels: { en: "Fashion & Accessories", fr: "Mode & accessoires", es: "Moda y accesorios", zh: "时尚与配饰" }
      },
      {
        label: "Kids & Baby",
        option: "Kids-Baby",
        icon: "user-smile-line",
        labels: { en: "Kids & Baby", fr: "Enfants & bébé", es: "Niños y bebés", zh: "儿童与婴儿" }
      },
      {
        label: "Medical & Accessories",
        option: "Medical-Accessories",
        icon: "first-aid-kit-line",
        labels: { en: "Medical & Accessories", fr: "Médical & accessoires", es: "Médico y accesorios", zh: "医疗与配件" }
      },
      {
        label: "Miscellaneous",
        option: "Miscellaneous",
        icon: "apps-2-line",
        labels: { en: "Miscellaneous", fr: "Divers", es: "Varios", zh: "杂项" }
      },
      {
        label: "Moving",
        option: "Moving",
        icon: "box-3-line",
        labels: { en: "Moving", fr: "Déménagement", es: "Mudanzas", zh: "搬家" }
      },
      {
        label: "Toys, Games, Music, Books & Art",
        option: "Toys-Games-Music-Books-Art",
        icon: "game-line",
        labels: { en: "Toys, Games, Music, Books & Art", fr: "Jouets, jeux, musique, livres & art", es: "Juguetes, juegos, música, libros y arte", zh: "玩具、游戏、音乐、书籍与艺术" }
      },
      {
        label: "Party & Event ",
        option: "Party-Event",
        icon: "calendar-event-line",
        labels: { en: "Party & Event", fr: "Fête & événement", es: "Fiesta y evento", zh: "派对与活动" }
      },
      {
        label: "Photography &  Video",
        option: "photography-video",
        icon: "camera-line",
        labels: { en: "Photography & Video", fr: "Photographie & vidéo", es: "Fotografía y video", zh: "摄影与视频" }
      },
      {
        label: "People &  Services",
        option: "People-Services",
        icon: "open-arm-line",
        labels: { en: "People & Services", fr: "Personnes & services", es: "Personas y servicios", zh: "人员与服务" }
      },
      {
        label: "Recreational Vehicles",
        option: "Recreational-Vehicles",
        icon: "bike-line",
        labels: { en: "Recreational Vehicles", fr: "Véhicules de loisirs", es: "Vehículos recreativos", zh: "休闲车" }
      },
      {
        label: "Spaces, Studio's & Storage",
        option: "Spaces-Storage",
        icon: "store-line",
        labels: { en: "Spaces, Studio's & Storage", fr: "Espaces, studios & stockage", es: "Espacios, estudios y almacenamiento", zh: "空间、工作室与存储" }
      },
      {
        label: "Sports & Recreation",
        option: "Sports-Recreation",
        icon: "ping-pong-line",
        labels: { en: "Sports & Recreation", fr: "Sports & loisirs", es: "Deportes y recreación", zh: "运动与娱乐" }
      },
      {
        label: "Trailers & RV's",
        option: "RV-Trailers",
        icon: "caravan-line",
        labels: { en: "Trailers & RVs", fr: "Remorques & camping-cars", es: "Remolques y RVs", zh: "拖车与房车" }
      },
      {
        label: "Watercraft & Accessories",
        option: "watercraft-accessories",
        icon: "sailboat-line",
        labels: { en: "Watercraft & Accessories", fr: "Embarcations & accessoires", es: "Embarcaciones y accesorios", zh: "水上交通工具与配件" }
      }
    ],
    filterConfig: {
      indexForSearch: true,
      label: "Rental Category",
      filterType: "SelectMultipleFilter",
      group: "primary"
    },
    showConfig: {
      isDetail: true,
      label: "Rental Category",
    },
    saveConfig: {
      sectionTitle: "Rental Category",
      label: "Select the category that best fits your item",
      isRequired: true,
      requiredMessage: "Rental Category is required",
      showRegenerate: false,
      showToggle: false,
      showCredits: false,
      showChevron: true,
      columns: 3
    },
    includeForListingTypes: [
      "general"
    ]
  },
  {
    key: "Condition_of_the_item",
    scope: "public",
    schemaType: "enum",
    enumOptions: [
      {
        label: "New",
        subLabel: "Never used",
        option: "New"
      },
      {
        label: "Used",
        subLabel: "Like New",
        option: "Used_Like_New"
      },
      {
        label: "Used",
        subLabel: "Good",
        option: "Used_Good"
      },
      {
        label: "Used",
        subLabel: "Fair",
        option: "Used_Fair"
      },
      {
        label: "It's The Rental of a Service",
        subLabel: "Offering a service",
        option: "its_a_service"
      }
    ],
    filterConfig: {
      indexForSearch: true,
      label: "Condition",
      filterType: "SelectMultipleFilter",
      group: "primary"
    },
    showConfig: {
      label: "Condition of item",
      isDetail: true,
    },
    saveConfig: {
      sectionTitle: "Condition",
      label: "Condition of your item",
      isRequired: true,
      requiredMessage: "Condition of your item is required",
      showRegenerate: false,
      showToggle: false,
      showCredits: false,
      showChevron: true,
      columns: 5
    },
    includeForListingTypes: [
      "general"
    ]
  },
  {
    key: "access_to_rentals",
    scope: "public",
    schemaType: "multi-enum",
    enumOptions: [
      {
        label: "Delivery Available",
        option: "delivery_available"
      },
      {
        label: "Return Pickup",
        option: "return_pickup"
      },
      {
        label: "At Owner's Location",
        option: "Pickupat_owners_location"
      },
      {
        label: "Public Meetup Location",
        option: "public_meetup_location"
      },
      {
        label: "Shipping (FedEX, UPS, etc.)",
        option: "shipping"
      },
      {
        label: "At Renter's Location",
        option: "service_where_required"
      },
      {
        label: "Digitally",
        option: "digitally"
      }
    ],
    additionalOptions: {
      whenToApply: [
        { label: "Optional When Booking", option: "requiredUpfrontOptional", disabled: false },
        { label: "Required When Booking", option: "requiredUpfront", disabled: false },
        // { label: "Optional Throughout the Booking (coming soon)", option: "optionalThroughout", disabled: true },
      ],
      feeCalculationType: [
        { label: "Flat Fee $", option: "flatRate", disabled: false },
        { label: "% of rental amount", option: "percentageOfRental", disabled: false },
        { label: "Usage multiple", option: "usageMultiple", disabled: false }
      ],
      checkoutOptions: [
        { label: "Only charge once for the entire cart", option: "oncePerCart", disabled: false },
        { label: "Charge For This Item Individually", option: "oncePerItem", disabled: false }
        // { label: "Charged once per listing", option: "oncePerListing", disabled: false },
      ]
    },
    filterConfig: {
      indexForSearch: true,
      label: "Rental Accessibility",
      filterType: "SelectMultipleFilter",
      searchMode: "has_all",
      group: "primary"
    },
    showConfig: {
      label: "Rental Accessibility",
      isDetail: true
    },
    saveConfig: {
      sectionTitle: "Rental Accessibility",
      label: "Select how renters can receive or access your item. You can also set optional fees for delivery and pickup where applicable.",
      isRequired: true,
      requiredMessage: "Rental Accessibility is required",
      showRegenerate: false,
      showToggle: false,
      showCredits: false,
      showChevron: true,
      columns: 1
    },
    includeForListingTypes: [
      "general"
    ]
  },
  {
    key: "Trust_and_verification",
    scope: "public",
    schemaType: "enum",
    enumOptions: [
      {
        label: "Not Required",
        option: "not_required"
      },
      {
        label: "Minimum Of 3 Successful Rent Anything Rentals ",
        option: "minimum_of_3_successful_rent"
      },
      {
        label: "Copy Of Driver's License or Passport",
        option: "copy_of_drivers_licience"
      },
      {
        label: "Visual ID Verification",
        option: "visual_id_verification"
      }
    ],
    filterConfig: {
      indexForSearch: false,
      label: "Trust & Verification Requirements (Payments are securely held & verified through Stripe)",
      filterType: "SelectSingleFilter",
      group: "primary"
    },
    showConfig: {
      label: "Trust & Verification Requirements (Payments are securely held & verified through Stripe)",
      isDetail: true
    },
    saveConfig: {
      sectionTitle: "Trust & Verification Requirements",
      label: "Select verification requirements for renters",
      isRequired: true,
      requiredMessage: "Verification requirements are required",
      showRegenerate: false,
      showToggle: false,
      showCredits: false,
      showChevron: true,
      helperText: "Payments are securely held and verified through Stripe",
    },
    includeForListingTypes: [
      "general"
    ]
  },
  {
    key: "security_deposit",
    scope: "public",
    schemaType: "enum",
    enumOptions: [
      {
        label: "No Deposit Required",
        option: "no_deposit"
      },
      {
        label: "$50.00 Refundable Deposit",
        option: "50"
      },
      {
        label: "$100.00 Refundable Deposit",
        option: "100"
      },
      {
        label: "$250.00 Refundable Deposit",
        option: "250"
      },
      {
        label: "$500.00 Refundable Deposit",
        option: "500"
      }
    ],
    filterConfig: {
      indexForSearch: false,
      label: "Cash Security Deposit (during rental period only)",
      filterType: "SelectSingleFilter",
      group: "primary"
    },
    showConfig: {
      label: "Cash Security Deposit (during rental period only)",
      isDetail: true
    },
    saveConfig: {
      sectionTitle: "Cash Security Deposit",
      label: "Set a refundable security deposit (held during rental period only)",
      isRequired: true,
      requiredMessage: "Cash Security Deposit is required",
      showRegenerate: false,
      showToggle: false,
      showCredits: false,
      showChevron: true,
    },
    includeForListingTypes: [
      "general"
    ]
  },
  {
    key: "cancelation_policy",
    scope: "public",
    schemaType: "enum",
    enumOptions: [
      {
        label: "Easy Going",
        subLabel: "100% refund with 72 hours + notice",
        option: "easy-going"
      },
      {
        label: "Flexible",
        subLabel: "50% refund with 72 hours + notice.",
        option: "flexible"
      },
      {
        label: "Strict",
        subLabel: "No refund provided on booked rentals.",
        option: "strict"
      }
    ],
    filterConfig: {
      indexForSearch: false,
      label: "Cancellation Policy",
      filterType: "SelectSingleFilter",
      group: "primary"
    },
    showConfig: {
      label: "Cancellation Policy",
      isDetail: true
    },
    saveConfig: {
      sectionTitle: "Cancellation Policy",
      label: "Set a Cancellation Policy",
      isRequired: true,
      requiredMessage: "Cancellation Policy is required",
      showRegenerate: false,
      showToggle: false,
      showCredits: false,
      showChevron: true,
      columns: 3
    },
    includeForListingTypes: [
      "general"
    ]
  },
  {
    key: "reason_for_listing",
    scope: "public",
    schemaType: "multi-enum",
    enumOptions: [
      {
        label: "Help the environment ",
        option: "help_the_environment"
      },
      {
        label: "Offer as a \"try before you buy\"",
        option: "try_before-you_buy"
      },
      {
        label: "It's a side hustle",
        option: "side_hustle"
      },
      {
        label: "In the rental business",
        option: "in_the_rental_business"
      },
      {
        label: "Just because",
        option: "just_because"
      }
    ],
    filterConfig: {
      indexForSearch: true,
      label: "Reason For Renting",
      filterType: "SelectMultipleFilter",
      searchMode: "has_all",
      group: "primary"
    },
    showConfig: {
      label: "Reason For Renting",
      isDetail: true
    },
    saveConfig: {
      sectionTitle: "Reason For Renting",
      label: "Select your reason for renting out this item",
      isRequired: true,
      requiredMessage: "Reason For Renting is required",
      showRegenerate: false,
      showToggle: false,
      showCredits: false,
      showChevron: true,
      columns: 3,
      showEnvironmentText: true,
    },
    includeForListingTypes: [
      "general"
    ]
  },

  // Additional fields in the AI listing process
  // 1. Alternative Search Titles
  // 2. Where the item can be used
  // 3. Suggested usage of this item
  // 4. Replacement value
  // 5. FAQs
  // 6. Online Review Summary
  {
    key: "alternative_search_titles",
    scope: "public",
    schemaType: "multi-enum",
    enumOptions: [], //Will be populated by AI
    filterConfig: {
      indexForSearch: true,
      label: "Alternative Search Titles",
      filterType: "SelectMultipleFilter",
      searchMode: "has_all",
      group: "primary"
    },
    showConfig: {
      label: "Alternative Search Titles",
      isDetail: true
    },
    saveConfig: {
      sectionTitle: "Alternative Search Titles",
      label: "Select additional titles to improve discoverability",
      isRequired: false,
      showRegenerate: true,
      showToggle: true,
      showCredits: true,
      showChevron: true,
      columns: 1
    },
    includeForListingTypes: [
      "general"
    ]
  },
  {
    key: "where_the_item_can_be_used",
    scope: "public",
    schemaType: "text",
    enumOptions: [], //Will be populated by AI
    filterConfig: {
      indexForSearch: true,
      label: "Where the item can be used",
      filterType: "TextAreaFilter",
      group: "primary"
    },
    showConfig: {
      label: "Where the item can be used",
      isDetail: true
    },
    saveConfig: {
      sectionTitle: "Where the item can be used",
      label: "Help renters picture where your item works best - like indoor, outdoor, events, etc.",
      isRequired: false,
      showRegenerate: true,
      showToggle: true,
      showCredits: true,
      showChevron: true,
    },
    includeForListingTypes: [
      "general"
    ]
  },
  {
    key: "suggested_usage_of_this_item",
    scope: "public",
    schemaType: "multi-enum",
    enumOptions: [], //Will be populated by AI
    filterConfig: {
      indexForSearch: true,
      label: "Suggested usage of this item",
      filterType: "SelectMultipleFilter",
      searchMode: "has_any",
      group: "primary"
    },
    showConfig: {
      label: "Suggested usage of this item",
      isDetail: true
    },
    saveConfig: {
      sectionTitle: "Suggested usage of this item",
      label: "Select practical or popular ways this item can be used to help renters quickly see the value.",
      showRegenerate: true,
      showToggle: true,
      showCredits: true,
      showChevron: true,
    },
    includeForListingTypes: [
      "general"
    ]
  },
  {
    key: "replacement_value",
    scope: "public",
    schemaType: "object",
    filterConfig: {
      indexForSearch: false,
      label: "Replacement value",
      filterType: "SelectMultipleFilter",
      group: "secondary"
    },
    showConfig: {
      label: "Replacement value",
      isDetail: true
    },
    saveConfig: {
      sectionTitle: "Replacement value",
      label: "Enter the min and max cost to replace your item",
      showRegenerate: false,
      showToggle: false,
      showCredits: false,
      showChevron: true,
      isRequired: true,
      requiredMessage: "Replacement value is required"
    },
    includeForListingTypes: [
      "general"
    ]
  },
  {
    key: "return_care_instructions",
    scope: "public",
    schemaType: "multi-enum",
    enumOptions: [], //Will be populated by AI
    filterConfig: {
      indexForSearch: true,
      label: "Return care instructions",
      filterType: "SelectMultipleFilter",
      searchMode: "has_all",
      group: "primary"
    },
    showConfig: {
      label: "Return care instructions",
      isDetail: true
    },
    saveConfig: {
      sectionTitle: "Return care instructions",
      label: "Select return care instructions to help renters understand how to return your item",
      showRegenerate: true,
      showToggle: true,
      showCredits: true,
      showChevron: true,
    },
    includeForListingTypes: [
      "general"
    ],
    requiredMessage: "ListingForm.validation.required"
  },
  {
    key: "faqs",
    scope: "public",
    schemaType: "multi-enum",
    enumOptions: [], //Will be populated by AI
    filterConfig: {
      indexForSearch: true,
      label: "FAQs",
      filterType: "SelectMultipleFilter",
      searchMode: "has_all",
      group: "primary"
    },
    showConfig: {
      label: "FAQs",
      isDetail: true
    },
    saveConfig: {
      sectionTitle: "Frequently Asked Questions",
      label: "Select questions renters might ask to help them decide faster",
      showRegenerate: true,
      showToggle: true,
      showCredits: true,
      showChevron: true,
    },
    includeForListingTypes: [
      "general"
    ],
    requiredMessage: "ListingForm.validation.required"
  },
  {
    key: "online_review_summary",
    scope: "public",
    schemaType: "text",
    filterConfig: {
      indexForSearch: false,
      label: "Online Review Summary",
      filterType: "TextAreaFilter",
      group: "primary"
    },
    showConfig: {
      label: "Online Review Summary",
      isDetail: true
    },
    saveConfig: {
      sectionTitle: "Online Review Summary",
      label: "A short summary from trusted public sources to help renters understand the item's reputation.",
      showRegenerate: true,
      showToggle: true,
      showCredits: true,
      showChevron: true,
      isRequired: false,
    },
    includeForListingTypes: [
      "general"
    ]
  }
]



export const optionalFields = [
  "alternative_search_titles",
  "instructions",
  "where_the_item_can_be_used",
  "suggested_usage_of_this_item",
  "faqs",
  "online_review_summary"
]

export const defaultReviewCount = 2;
export const defaultRating = 5;